/**
 * GraphsTimeRangePicker Component
 * Custom time range picker with 24-hour maximum constraint
 *
 * Requirements:
 * - 5.1: Default to last 24 hours from current time
 * - 5.2: Prevent selection of ranges exceeding 24 hours with validation message
 * - 5.3: Update only Graphs tab data without affecting global time range settings
 * - 5.4: Preserve time range across tab switches within session
 */

import React, { useState, useCallback, useMemo } from 'react';
import { QICustomDateRangePicker } from '../../../components';
import { validateTimeRange, getDefault24HourRange } from './utils';
import { MAX_TIME_RANGE_HOURS } from './constants';
import moment from 'moment';

/**
 * @param {Object} props
 * @param {Object} props.selectedRange - Current selected range { startDate, endDate }
 * @param {Function} props.onRangeChange - Callback when range changes
 * @param {number} props.maxRangeHours - Maximum allowed hours (default: 24)
 */
export const GraphsTimeRangePicker = ({
  selectedRange,
  onRangeChange,
  maxRangeHours = MAX_TIME_RANGE_HOURS,
}) => {
  // Validation error state
  const [validationError, setValidationError] = useState('');

  // Local reset counter for the date picker component
  const [resetCount, setResetCount] = useState(0);

  // Ensure selectedRange has valid moment objects for the date picker
  const normalizedRange = useMemo(() => {
    if (!selectedRange) {
      return getDefault24HourRange();
    }
    return {
      startDate: moment(selectedRange.startDate),
      endDate: moment(selectedRange.endDate),
    };
  }, [selectedRange]);

  // Handle range change with validation
  // This is called by QICustomDateRangePicker via setSelectedRangeLocal prop
  const handleRangeChange = useCallback(
    (newRange) => {
      // Validate the new range
      const validation = validateTimeRange(newRange, maxRangeHours);

      if (!validation.isValid) {
        // Set validation error message (Requirements 5.2)
        setValidationError(validation.message);
        return;
      }

      // Clear any previous error
      setValidationError('');

      // Apply the new range via callback (Requirements 5.3 - local state only)
      if (onRangeChange) {
        onRangeChange({
          startDate: moment(newRange.startDate),
          endDate: moment(newRange.endDate),
        });
      }
    },
    [onRangeChange, maxRangeHours]
  );

  // Reset to default 24-hour range
  const handleReset = useCallback(() => {
    setValidationError('');
    const defaultRange = getDefault24HourRange();
    if (onRangeChange) {
      onRangeChange(defaultRange);
    }
    setResetCount((prev) => prev + 1);
  }, [onRangeChange]);

  return (
    <div className="graphs-time-range-picker">
      <div className="graphs-time-range-picker__controls">
        <QICustomDateRangePicker
          selectedRange={normalizedRange}
          setSelectedRangeLocal={handleRangeChange}
          localRange={true}
          resetCount={resetCount}
          setResetCount={setResetCount}
          showDefaultRanges={true}
          className="graphs-date-picker"
        />
        <button
          type="button"
          className="btn btn-sm btn-outline graphs-reset-btn"
          onClick={handleReset}
          title="Reset to last 24 hours"
        >
          Reset
        </button>
      </div>

      {validationError && (
        <div className="graphs-time-range-picker__error" role="alert">
          {validationError}
        </div>
      )}

      <div className="graphs-time-range-picker__info">
        <small>Maximum time range: {maxRangeHours} hours</small>
      </div>
    </div>
  );
};

export default GraphsTimeRangePicker;
